// Lyssnar på ändringar i projektets dokumenttabeller så att listor och
// arbetsmiljöplanen uppdateras automatiskt – utan manuell omladdning.
import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export const REALTIME_DOCUMENT_TABLES = [
  "folder_risk_assessments",
  "skr_assessments",
  "work_preparations",
  "amp_sections",
] as const;

export type RealtimeDocumentTable = (typeof REALTIME_DOCUMENT_TABLES)[number];

interface Options {
  /** Vilka tabeller som ska bevakas. Standard: alla dokumenttabeller. */
  tables?: readonly RealtimeDocumentTable[];
  /** Hur länge ändringar buntas ihop innan omhämtning (ms). */
  debounceMs?: number;
  enabled?: boolean;
}

/**
 * Kör `onChange` när något i projektet ändras (live), samt när fliken får
 * fokus igen – så att vyn hämtar om även om realtidskopplingen tappats.
 * Returnerar tidpunkten för senaste automatiska uppdateringen.
 */
export function useProjectRealtime(
  projectId: string | null | undefined,
  onChange: () => void | Promise<void>,
  options: Options = {},
) {
  const { tables = REALTIME_DOCUMENT_TABLES, debounceMs = 600, enabled = true } = options;
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date | null>(null);
  const handlerRef = useRef(onChange);
  handlerRef.current = onChange;

  const tableKey = tables.join(",");

  useEffect(() => {
    if (!projectId || !enabled) return;

    let timer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const trigger = (markUpdated: boolean) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        if (cancelled) return;
        if (markUpdated) setLastUpdatedAt(new Date());
        void handlerRef.current();
      }, debounceMs);
    };

    const channel = supabase.channel(`project-docs-${projectId}-${Math.random().toString(36).slice(2)}`);

    for (const table of tableKey.split(",") as RealtimeDocumentTable[]) {
      channel.on(
        "postgres_changes",
        { event: "*", schema: "public", table, filter: `project_id=eq.${projectId}` },
        () => trigger(true),
      );
    }

    channel.subscribe();

    const onFocus = () => {
      if (document.visibilityState === "visible") trigger(false);
    };
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", onFocus);

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("focus", onFocus);
      void supabase.removeChannel(channel);
    };
  }, [projectId, enabled, debounceMs, tableKey]);

  return { lastUpdatedAt };
}

export default useProjectRealtime;
